import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'
import { TooltipContentProps } from 'recharts'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { CentToFormattedAmount } from 'modules/finance/components/FinancialWorkspace/components/chart/helpers'
import { PARSED_ITEMS } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/chart/helpers'

import { Comparisons } from './Comparisons'
import { Current } from './Current'
import { Growth } from './Growth'
import { Titles } from './Titles'

interface ToolTipProps {
  items: PARSED_ITEMS
  tooltipProps: TooltipContentProps<string, number>
  toAmount: (value: number) => CentToFormattedAmount
}

const Tooltip = ({ items, tooltipProps, toAmount }: ToolTipProps) => {
  const { formattedState: variables, hasComparisonDate } = useFinancialWorkspace()
  const { formattedStartDate, formattedCompareToStartDate } = variables

  const start = moment(formattedStartDate)
  const compareToStartDate = moment(formattedCompareToStartDate)

  const labels: string[] = Object.values(items)
    .find((data) => {
      return data?.some((obj: { x: string }) => obj?.x === tooltipProps.label)
    })
    ?.map((obj: { x: string }) => obj.x)

  const index = labels?.indexOf(`${tooltipProps.label}`)
  if (index === undefined) return null

  const {
    LABOR_COST,
    AGREEMENT_DATA,
    TOTAL_EXPENSES,
    EMPLOYEE_EXPENSES,
    COMPARISON_LABOR_COST,
    COMPARISON_AGREEMENT_DATA,
    COMPARISON_TOTAL_EXPENSES,
    COMPARISON_EMPLOYEE_EXPENSES,
  } = items

  const hasTotal = !!TOTAL_EXPENSES?.[index]?.y
  const hasLaborCost = !!LABOR_COST?.[index]?.y
  const hasAgreementData = !!AGREEMENT_DATA?.[index]?.y
  const hasEmployeeExpenses = !!EMPLOYEE_EXPENSES?.[index]?.y

  const hasComparisonTotal = !!COMPARISON_TOTAL_EXPENSES?.[index]?.y
  const hasComparisonLaborCost = !!COMPARISON_LABOR_COST?.[index]?.y
  const hasComparisonAgreementData = !!COMPARISON_AGREEMENT_DATA?.[index]?.y
  const hasComparisonEmployeeExpenses = !!COMPARISON_EMPLOYEE_EXPENSES?.[index]?.y

  const showTotal = hasTotal || hasComparisonTotal
  const showLaborCost = hasLaborCost || hasComparisonLaborCost
  const showAgreementData = hasAgreementData || hasComparisonAgreementData
  const showEmployeeExpenses = hasEmployeeExpenses || hasComparisonEmployeeExpenses
  const showDivider = showAgreementData && (showLaborCost || showEmployeeExpenses)

  const isSameYear = hasComparisonDate && start.isSame(compareToStartDate, 'year')

  return (
    <Box
      gap="s16"
      padding="s12"
      background="white"
      width="fit-content"
      borderRadius={{ all: 'abs016' }}
      style={{ boxShadow: '0px 4px 20px 0px #0D162514' }}
      border={{ all: { color: 'grey200', width: 's1', style: 'solid' } }}
    >
      <Box flexDirection="row" gap="s0" justifyContent="spaceBetween">
        <Titles
          showTotal={showTotal}
          showDivider={showDivider}
          showLaborCost={showLaborCost}
          showAgreementData={showAgreementData}
          showEmployeeExpenses={showEmployeeExpenses}
          label={isSameYear ? formattedStartDate.slice(0, 4) : `${tooltipProps.label}`}
        />
        {hasComparisonDate && (
          <Comparisons
            items={items}
            index={index}
            toAmount={toAmount}
            showTotal={showTotal}
            showDivider={showDivider}
            showLaborCost={showLaborCost}
            showAgreementData={showAgreementData}
            showEmployeeExpenses={showEmployeeExpenses}
            label={compareToStartDate.format(isSameYear ? 'MMM' : ' YYYY')}
          />
        )}
        <Current
          items={items}
          index={index}
          toAmount={toAmount}
          showTotal={showTotal}
          showDivider={showDivider}
          showLaborCost={showLaborCost}
          hasComparisonDate={hasComparisonDate}
          showAgreementData={showAgreementData}
          showEmployeeExpenses={showEmployeeExpenses}
          label={start.format(isSameYear ? 'MMM' : ' YYYY')}
        />
        {hasComparisonDate && (
          <Growth
            items={items}
            index={index}
            showTotal={showTotal}
            showDivider={showDivider}
            showLaborCost={showLaborCost}
            showAgreementData={showAgreementData}
            showEmployeeExpenses={showEmployeeExpenses}
          />
        )}
      </Box>
    </Box>
  )
}

export { Tooltip }
